import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { InputBox } from '../FormComponent';
import styles from '../../styles/automation/index.module.scss';
import {
    convertIdsToDisplay as convertIdsToDisplayUtil,
    convertDisplayToIds as convertDisplayToIdsUtil,
    migrateOldVariableFormat
} from '../../utils/automation/variableUtils';

// Memoized dropdown item for variables
const DropdownItem = memo(({ 
    item, 
    index, 
    activeIndex, 
    descriptions, 
    onSelect, 
    onMouseEnter 
}) => (
    <li
        key={item.id}
        onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(item);
        }}
        onMouseEnter={() => onMouseEnter(index)}
        className={`${styles.dropdownItem} ${index === activeIndex ? styles.active : ''}`}
        tabIndex={0}
        aria-selected={index === activeIndex}
        onFocus={() => onMouseEnter(index)}
        onKeyDown={(e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                onSelect(item);
            }
        }}
    >
        <span
            className={`${styles.variableIcon} ${descriptions[item.id]?.isSecret ? styles.secret : styles.normal}`}
            title={descriptions[item.id]?.systemVariable ? 'Project Variable' : 'Environment Variable'}
        >
            {descriptions[item.id]?.systemVariable ? 'P' : 'E'}
        </span>
        ${item.key}
    </li>
));

// Memoized dropdown item for files (shows file name)
const FileDropdownItem = memo(({ 
    item, 
    index, 
    activeIndex, 
    onSelect, 
    onMouseEnter 
}) => (
    <li
        key={item.id}
        onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(item);
        }}
        onMouseEnter={() => onMouseEnter(index)}
        className={`${styles.dropdownItem} ${index === activeIndex ? styles.active : ''}`}
        tabIndex={0}
        aria-selected={index === activeIndex}
        onFocus={() => onMouseEnter(index)}
        onKeyDown={(e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                onSelect(item);
            }
        }}
    >
        <span className={styles.fileItemIcon} title="File">
            F
        </span>
        {item.name}
    </li>
));

const SmartInputBox = ({
    variableTrigger = '${',
    placeholder = 'Type ${ to add variables',
    suggestions = [],
    descriptions = {},
    /** File dropdown: trigger (e.g. "@"), list of files, descriptions by id */
    fileTrigger = '',
    fileSuggestions = [],
    fileDescriptions = {},
    /** Optional: (file) => string for inserted text; default "@[id]" */
    formatFileInsertion,
    value = '',
    onChange,
    onBlur,
    onKeyDown: parentOnKeyDown,
    ...inputBoxProps
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownMode, setDropdownMode] = useState(null); // 'variables' | 'files'
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    
    // Refs for DOM elements
    const inputRef = useRef(null);
    const dropdownListRef = useRef(null);
    const dropdownPortalRef = useRef(null);
    const containerRef = useRef(null);
    const migratedValueRef = useRef(null); // Track if we've migrated this value
    
    // Current list and count based on dropdown mode
    const activeList = dropdownMode === 'variables' ? suggestions : dropdownMode === 'files' ? fileSuggestions : [];
    const activeLength = activeList.length;

    // Reset selected index and mode when dropdown closes
    useEffect(() => {
        if (!showDropdown) {
            setSelectedIndex(0);
            setDropdownMode(null);
        }
    }, [showDropdown]);

    // Memoized conversion functions using centralized utilities
    const convertIdsToDisplay = useCallback((storedValue) => {
        return convertIdsToDisplayUtil(storedValue, suggestions, descriptions);
    }, [suggestions, descriptions]);

    // File refs: stored @[id] <-> display file name (id normalized for UUID/case/whitespace)
    const fileRefsToDisplay = useCallback((str) => {
        if (!str || typeof str !== 'string' || fileSuggestions.length === 0) return str;
        const norm = (x) => String(x).trim().toLowerCase();
        const byId = new Map(fileSuggestions.map((f) => [norm(f.id), f.name]));
        return str.replace(/@\[([^\]]+)\]/g, (_, id) => byId.get(norm(id)) ?? `@[${id}]`);
    }, [fileSuggestions]);

    const fileNamesToRefs = useCallback((str) => {
        if (!str || typeof str !== 'string' || fileSuggestions.length === 0) return str;
        const sorted = [...fileSuggestions].sort((a, b) => (b.name?.length ?? 0) - (a.name?.length ?? 0));
        let out = str;
        for (const f of sorted) {
            if (f.name) {
                const escaped = f.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                out = out.replace(new RegExp(escaped, 'g'), `@[${f.id}]`);
            }
        }
        return out;
    }, [fileSuggestions]);

    const convertDisplayToIds = useCallback((displayValue) => {
        const withFileRefs = fileNamesToRefs(displayValue);
        return convertDisplayToIdsUtil(withFileRefs, suggestions, descriptions);
    }, [suggestions, descriptions, fileNamesToRefs]);


    // Migrate old format to IDs when value changes (backward compatibility)
    useEffect(() => {
        const needsMigration = value && 
            typeof value === 'string' && 
            migratedValueRef.current !== value &&
            migrateOldVariableFormat(value, suggestions, descriptions) !== value;
        
        if (needsMigration) {
            const convertedValue = migrateOldVariableFormat(value, suggestions, descriptions);
            
            if (convertedValue !== value && onChange && inputRef.current) {
                migratedValueRef.current = value; // Mark as migrated
                const syntheticEvent = {
                    target: {
                        value: convertedValue,
                        name: inputRef.current.name
                    }
                };
                onChange(syntheticEvent);
            }
        } else if (!value || typeof value !== 'string' || !value.includes('${')) {
            // Reset migration tracking if value doesn't need migration (guard: value may be object from step args)
            migratedValueRef.current = null;
        }
    }, [value, onChange, suggestions, descriptions]);

    // Display value: variables to display format, @[id] to file name
    const displayValue = useMemo(() => {
        const withVariables = convertIdsToDisplay(value);
        return fileRefsToDisplay(withVariables);
    }, [value, convertIdsToDisplay, fileRefsToDisplay]);


    // Calculate dropdown position dynamically
    const calculateDropdownPosition = useCallback(() => {
        const input = inputRef.current;
        if (!input) return { top: 0, left: 0 };
        
        const inputRect = input.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Dropdown dimensions (matching SCSS)
        const dropdownWidth = window.innerWidth >= 1440 ? 450 : 400;
        const dropdownHeight = window.innerWidth >= 1440 ? 220 : 200;
        const spacing = window.innerWidth >= 1440 ? 6 : 4;
        
        // Calculate initial position (below input)
        let top = inputRect.bottom + spacing;
        let left = inputRect.left;
        
        // Adjust horizontal position if dropdown would go off-screen
        if (left + dropdownWidth > viewportWidth - 20) {
            left = viewportWidth - dropdownWidth - 20;
        }
        if (left < 20) {
            left = 20;
        }
        
        // Adjust vertical position if dropdown would go off-screen
        if (top + dropdownHeight > viewportHeight - 20) {
            // Show above input instead
            top = inputRect.top - dropdownHeight - spacing;
            
            // If still off-screen, position at top of viewport
            if (top < 20) {
                top = 20;
            }
        }
        
        return { top, left };
    }, []);

    // Update dropdown position when shown
    useEffect(() => {
        if (showDropdown) {
            const position = calculateDropdownPosition();
            setDropdownPosition(position);
        }
    }, [showDropdown, calculateDropdownPosition]);

    // Detect which dropdown (if any) should show based on trigger typed
    const getDropdownState = useCallback((val, cursor) => {
        if (variableTrigger && cursor >= variableTrigger.length) {
            const slice = val.slice(cursor - variableTrigger.length, cursor);
            if (slice === variableTrigger && suggestions.length > 0) {
                return { show: true, mode: 'variables' };
            }
        }
        if (fileTrigger && cursor >= fileTrigger.length) {
            const slice = val.slice(cursor - fileTrigger.length, cursor);
            if (slice === fileTrigger && fileSuggestions.length > 0) {
                return { show: true, mode: 'files' };
            }
        }
        return { show: false, mode: null };
    }, [variableTrigger, fileTrigger, suggestions.length, fileSuggestions.length]);

    // Handle input change
    const handleChange = useCallback((e) => {
        const input = e.target;
        const displayValue = e.target.value;
        
        // Ensure input reference is set
        if (!inputRef.current) {
            inputRef.current = input;
        }
        
        // Convert display format to IDs before saving
        const storedValue = convertDisplayToIds(displayValue);
        
        // Create synthetic event with stored value (IDs)
        const syntheticEvent = {
            target: {
                ...e.target,
                value: storedValue
            }
        };
        
        // Update the parent component with stored value (IDs)
        onChange(syntheticEvent);
        
        // Use setTimeout to ensure cursor position is updated after the change
        setTimeout(() => {
            const cursor = input.selectionStart;
            const { show, mode } = getDropdownState(displayValue, cursor);
            setShowDropdown(show);
            setDropdownMode(mode);
            if (show) setSelectedIndex(0);
        }, 0);
    }, [onChange, getDropdownState, convertDisplayToIds]);

    // Handle blur - convert display value to stored format before calling parent's onBlur
    const handleBlur = useCallback((e) => {
        const displayValue = e.target.value;
        
        // Convert display format to stored format (IDs)
        const storedValue = convertDisplayToIds(displayValue);
        
        // Create synthetic event with stored value (IDs)
        const syntheticEvent = {
            target: {
                ...e.target,
                value: storedValue
            }
        };
        
        // Call parent's onBlur with stored value
        if (onBlur) {
            onBlur(syntheticEvent);
        }
    }, [onBlur, convertDisplayToIds]);

    // Helper: find variable boundaries at a given cursor position
    function findVariableAtCursor(value, cursor) {
        let varStart = value.lastIndexOf('${', cursor);
        if (varStart === -1) return null;
        let varEnd = value.indexOf('}', varStart);
        if (varEnd === -1) return null;
        varEnd += 1;
        if (cursor > varStart && cursor < varEnd) {
            return { varStart, varEnd };
        }
        return null;
    }

    // Helper: find file ref boundaries (e.g. @[id]) at cursor in stored value
    const findFileRefAtCursor = useCallback((value, cursor) => {
        if (!fileTrigger) return null;
        const triggerLen = fileTrigger.length;
        let refStart = value.lastIndexOf(fileTrigger, cursor);
        if (refStart === -1) return null;
        if (refStart + triggerLen < value.length && value[refStart + triggerLen] !== '[') return null;
        const bracketStart = refStart + triggerLen;
        const bracketEnd = value.indexOf(']', bracketStart);
        if (bracketEnd === -1) return null;
        const refEnd = bracketEnd + 1;
        if (cursor > refStart && cursor < refEnd) {
            return { varStart: refStart, varEnd: refEnd };
        }
        return null;
    }, [fileTrigger]);

    // Helper: find file name segment at cursor in display value (user sees file names)
    const findFileSegmentInDisplay = useCallback((displayStr, cursor) => {
        if (!fileSuggestions.length) return null;
        for (const f of fileSuggestions) {
            if (!f.name) continue;
            let idx = 0;
            while (idx < displayStr.length) {
                const start = displayStr.indexOf(f.name, idx);
                if (start === -1) break;
                const end = start + f.name.length;
                if (cursor >= start && cursor <= end) {
                    return { varStart: start, varEnd: end };
                }
                idx = end;
            }
        }
        return null;
    }, [fileSuggestions]);

    // Scroll selected item into view when selectedIndex changes
    const scrollSelectedIntoView = useCallback((index) => {
        if (dropdownListRef.current) {
            const li = dropdownListRef.current.children[index];
            if (li) {
                li.scrollIntoView({ block: 'nearest' });
            }
        }
    }, []);

    // Insert selected file at caret (for file dropdown)
    const insertFile = useCallback((fileItem) => {
        if (!fileItem || !inputRef.current) return;
        const input = inputRef.current;
        const currentDisplayValue = input.value;
        const cursor = input.selectionStart;
        let before = currentDisplayValue.slice(0, cursor);
        const after = currentDisplayValue.slice(cursor);
        if (fileTrigger && before.endsWith(fileTrigger)) {
            before = before.slice(0, -fileTrigger.length);
        }
        const insertionStored = typeof formatFileInsertion === 'function'
            ? formatFileInsertion(fileItem)
            : `@[${fileItem.id}]`;
        const beforeStored = convertDisplayToIds(before);
        const afterStored = convertDisplayToIds(after);
        const newStoredValue = beforeStored + insertionStored + afterStored;
        onChange({ target: { value: newStoredValue, name: input.name } });
        setShowDropdown(false);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!input) return;
                input.focus();
                // Display shows file name, not @[id]; cursor after inserted file name
                const displayInsertLength = fileItem.name?.length ?? insertionStored.length;
                const newCursor = before.length + displayInsertLength;
                const safeCursor = Math.min(newCursor, (input.value || '').length);
                if (safeCursor >= 0) {
                    input.selectionStart = input.selectionEnd = safeCursor;
                }
            });
        });
    }, [fileTrigger, formatFileInsertion, onChange, convertDisplayToIds]);

    // Insert selected variable at caret (for variable dropdown)
    const insertVariable = useCallback((tokenObj) => {
        if (!tokenObj || !tokenObj.id) return;
        const input = inputRef.current;
        if (!input) return;
        const currentDisplayValue = input.value;
        const cursor = input.selectionStart;
        let before = currentDisplayValue.slice(0, cursor);
        let after = currentDisplayValue.slice(cursor);
        if (before.endsWith(variableTrigger)) {
            before = before.slice(0, -variableTrigger.length);
        }
        const prefix = descriptions[tokenObj.id]?.systemVariable ? 'PROJECT.' : 'ENV.';
        const insertedDisplayText = `\${${prefix}${tokenObj.key}}`;
        const beforeStored = convertDisplayToIds(before);
        const afterStored = convertDisplayToIds(after);
        const storedFormat = `\${_id:${tokenObj.id}}`;
        const newStoredValue = beforeStored + storedFormat + afterStored;
        onChange({ target: { value: newStoredValue, name: input.name } });
        setShowDropdown(false);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!input) return;
                input.focus();
                const newCursor = before.length + insertedDisplayText.length;
                const safeCursor = Math.min(newCursor, (input.value || '').length);
                if (safeCursor >= 0) {
                    input.selectionStart = input.selectionEnd = safeCursor;
                }
            });
        });
    }, [descriptions, onChange, convertDisplayToIds, variableTrigger]);

    // Insert selected token (variable or file) based on dropdown mode
    const insertToken = useCallback((item) => {
        if (dropdownMode === 'files') {
            insertFile(item);
        } else {
            insertVariable(item);
        }
    }, [dropdownMode, insertFile, insertVariable]);

    // Handle keyboard events for both dropdown navigation and variable deletion
    const handleKeyDown = useCallback((e) => {
        // Call parent's onKeyDown handler first to allow it to stop propagation if needed
        if (parentOnKeyDown) {
            parentOnKeyDown(e);
        }

        // Dropdown navigation
        if (showDropdown && activeLength > 0) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((prev) => {
                    const next = e.key === 'ArrowDown'
                        ? (prev + 1) % activeLength
                        : prev === 0 ? activeLength - 1 : prev - 1;
                    requestAnimationFrame(() => scrollSelectedIntoView(next));
                    return next;
                });
                return;
            } else if (e.key === 'Enter') {
                e.preventDefault();
                insertToken(activeList[selectedIndex]);
                return;
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setShowDropdown(false);
                return;
            }
        }

        // Variable deletion logic
        const input = inputRef.current;
        if (!input || input.selectionStart !== input.selectionEnd) return;

        const inputDisplayValue = input.value; // This is display format
        const cursor = input.selectionStart;
        const isBackspace = e.key === 'Backspace' && cursor > 0;
        const isDelete = e.key === 'Delete' && cursor < inputDisplayValue.length;

        if (isBackspace || isDelete) {
            const checkCursor = isBackspace ? cursor - 1 : cursor;
            let varInfo = findVariableAtCursor(inputDisplayValue, checkCursor);
            if (!varInfo) {
                varInfo = findFileSegmentInDisplay(inputDisplayValue, checkCursor);
            }
            if (!varInfo && findFileRefAtCursor) {
                varInfo = findFileRefAtCursor(inputDisplayValue, checkCursor);
            }
            if (varInfo) {
                e.preventDefault();
                const newDisplayValue = inputDisplayValue.slice(0, varInfo.varStart) + inputDisplayValue.slice(varInfo.varEnd);
                
                // Convert to stored format (IDs)
                const newStoredValue = convertDisplayToIds(newDisplayValue);
                
                // Create synthetic event for onChange with stored value (IDs)
                const syntheticEvent = {
                    target: {
                        value: newStoredValue,
                        name: input.name
                    }
                };
                
                onChange(syntheticEvent);
                requestAnimationFrame(() => {
                    input.focus();
                    input.selectionStart = input.selectionEnd = varInfo.varStart;
                });
            }
        }
    }, [showDropdown, activeList, activeLength, selectedIndex, insertToken, scrollSelectedIntoView, onChange, convertDisplayToIds, parentOnKeyDown, findFileRefAtCursor, findFileSegmentInDisplay]);

    // Handle click outside dropdown and cleanup on unmount
    const handleOutsideClick = useCallback((e) => {
        // Check if click is outside the input container
        const isOutsideInput = containerRef.current && !containerRef.current.contains(e.target);
        
        const isOutsideDropdown =
            !dropdownPortalRef.current || !dropdownPortalRef.current.contains(e.target);
        
        if (isOutsideInput && isOutsideDropdown) {
            setShowDropdown(false);
        }
    }, []);

    // Update position on window events
    const updatePosition = useCallback(() => {
        if (showDropdown) {
            const position = calculateDropdownPosition();
            setDropdownPosition(position);
        }
    }, [showDropdown, calculateDropdownPosition]);

    // Setup event listeners and cleanup
    useEffect(() => {
        if (showDropdown) {
            document.addEventListener('mousedown', handleOutsideClick);
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
            
            return () => {
                document.removeEventListener('mousedown', handleOutsideClick);
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition, true);
            };
        }
    }, [showDropdown, handleOutsideClick, updatePosition]);

    const activeIndex = Math.min(selectedIndex, Math.max(0, activeLength - 1));

    // List content: variables or files
    const dropdownListContent = useMemo(() => (
        <ul
            ref={dropdownListRef}
            className={styles.suggestionsList}
            tabIndex={0}
        >
            {dropdownMode === 'files'
                ? fileSuggestions.map((item, index) => (
                    <FileDropdownItem
                        key={item.id}
                        item={item}
                        index={index}
                        activeIndex={activeIndex}
                        onSelect={insertToken}
                        onMouseEnter={setSelectedIndex}
                    />
                ))
                : suggestions.map((item, index) => (
                    <DropdownItem
                        key={item.id}
                        item={item}
                        index={index}
                        activeIndex={activeIndex}
                        descriptions={descriptions}
                        onSelect={insertToken}
                        onMouseEnter={setSelectedIndex}
                    />
                ))}
        </ul>
    ), [dropdownMode, suggestions, fileSuggestions, activeIndex, descriptions, insertToken]);

    // Description panel: variables (KEY, Type, Encrypted) or files (File type, Description, Size, Folder)
    const dropdownDescriptionContent = useMemo(() => {
        if (dropdownMode === 'files') {
            const file = fileSuggestions[activeIndex];
            const desc = file ? fileDescriptions[file.id] : null;
            return (
                <div className={styles.variableDescription}>
                    <div className={styles.descriptionItem}>
                        <strong>File type</strong>
                        <div>{desc?.fileType ?? '—'}</div>
                    </div>
                    <div className={styles.descriptionItem}>
                        <strong>Description</strong>
                        <div>{desc?.description ?? '—'}</div>
                    </div>
                    <div className={styles.descriptionItem}>
                        <strong>Size</strong>
                        <div>{desc?.size ?? '—'}</div>
                    </div>
                    <div className={styles.descriptionItem}>
                        <strong>Folder</strong>
                        <div>{desc?.folder ?? '—'}</div>
                    </div>
                </div>
            );
        }
        return (
            <div className={styles.variableDescription}>
                <div className={styles.descriptionItem}>
                    <strong>KEY</strong>
                    <div>{descriptions[suggestions[activeIndex]?.id]?.key || '—'}</div>
                </div>
                <div className={styles.descriptionItem}>
                    <strong>Type</strong>
                    <div>
                        {descriptions[suggestions[activeIndex]?.id]?.systemVariable
                            ? 'Project Variable'
                            : 'Environment Variable'}
                    </div>
                </div>
                <div className={styles.descriptionItem}>
                    <strong>Encrypted</strong>
                    <div>
                        {descriptions[suggestions[activeIndex]?.id]?.isSecret ? 'Yes' : 'No'}
                    </div>
                </div>
            </div>
        );
    }, [dropdownMode, suggestions, fileSuggestions, activeIndex, descriptions, fileDescriptions]);

    const dropdownContent = (
        <div
            ref={dropdownPortalRef}
            className={styles.variableDropdownPortal}
            style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                zIndex: 9999,
            }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {dropdownListContent}
            {dropdownDescriptionContent}
        </div>
    );

    return (
        <div ref={containerRef} className={styles.smartInputContainer}>
            <InputBox
                {...inputBoxProps}
                value={displayValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                onFocus={(e) => {
                    // Set input reference when focused
                    inputRef.current = e.target;
                    if (inputBoxProps.onFocus) {
                        inputBoxProps.onFocus(e);
                    }
                }}
                customActionOnChange={(e) => {
                    // Ensure input reference is set
                    if (!inputRef.current) {
                        inputRef.current = e.target;
                    }
                }}
            />

            {/* Dropdown rendered in portal (variables or files) */}
            {showDropdown && activeLength > 0 &&
                createPortal(dropdownContent, document.body)
            }
        </div>
    );
};

export default SmartInputBox;
